import React, { useState, useEffect, useRef } from "react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Building2, Save, X, MapPin, Phone, Mail, FileText, Clock, Loader2, Crosshair, ChevronRight, ChevronDown, User, Navigation, Briefcase, Settings, Calendar, Layers } from 'lucide-react';
import SucursalForm from "./SucursalForm";
import HorariosLaboralesForm from "./HorariosLaboralesForm";

// Función auxiliar fuera del componente
function getHorariosDefecto() {
  return {
    lunes: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
    martes: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
    miercoles: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
    jueves: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
    viernes: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
    sabado: { activo: true, hora_inicio: '08:00', hora_fin: '12:00' },
    domingo: { activo: false, hora_inicio: '08:00', hora_fin: '12:00' }
  };
}

export default function ClienteFormMobile({ cliente, cerrar, recargar }) {
  const { user } = useAuth();
  const modalContentRef = useRef(null);

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const [form, setForm] = useState(
    cliente
      ? {
          ...cliente,
          sucursales: Array.isArray(cliente.sucursales) ? cliente.sucursales : [],
          horarios_laborales: cliente.horarios_laborales || getHorariosDefecto(),
          horas_acumulables: cliente.horas_acumulables || false,
          inicio_acumulacion_mes: cliente.inicio_acumulacion_mes || new Date().getMonth() + 1,
          inicio_acumulacion_anio: cliente.inicio_acumulacion_anio || new Date().getFullYear(),
          tipo_frecuencia_mantenimiento: cliente.tipo_frecuencia_mantenimiento || "mensual",
          dia_mantenimiento_preferido: cliente.dia_mantenimiento_preferido || "",
          monitor_url: cliente.monitor_url || "",
          departamentos: Array.isArray(cliente.departamentos) ? cliente.departamentos : [],
          configuracion_ubicacion: cliente.configuracion_ubicacion || { usa_departamentos: false, departamento_obligatorio: false, label_departamento: 'Área/Departamento' },
        }
      : {
          nombre: "",
          ruc: "",
          telefono: "",
          email: "",
          direccion: "",
          ciudad: "",
          tipo: "ocasional",
          estado: "activo",
          horas_contratadas: 0,
          sucursales: [],
          observaciones: "",
          horarios_laborales: getHorariosDefecto(),
          ubicacion_ciudad: "",
          ubicacion_departamento: "",
          ubicacion_pais: "Paraguay",
          horas_acumulables: false,
          inicio_acumulacion_mes: new Date().getMonth() + 1,
          inicio_acumulacion_anio: new Date().getFullYear(),
          tipo_frecuencia_mantenimiento: "mensual",
          dia_mantenimiento_preferido: "",
          monitor_url: "",
          departamentos: [],
          configuracion_ubicacion: { usa_departamentos: false, departamento_obligatorio: false, label_departamento: 'Área/Departamento' },
        }
  );

  // Estados para secciones colapsables en móvil
  const [seccionAbierta, setSeccionAbierta] = useState('basicos');
  const [autoUbicando, setAutoUbicando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Estados para validación en tiempo real
  const [errores, setErrores] = useState({
    ruc: "",
    email: ""
  });

  const secciones = {
    basicos: "Datos Básicos",
    contacto: "Contacto", 
    ubicacion: "Ubicación",
    horarios: "Horarios",
    sucursales: "Sucursales",
    departamentos: "Departamentos"
  };

  // Estados adicionales para funcionalidad completa
  const [nuevaSucursal, setNuevaSucursal] = useState("");
  const [nuevoDepartamento, setNuevoDepartamento] = useState({ nombre: "", sucursal_nombre: "" });

  // Funciones de validación en tiempo real
  const validarRUC = (valor) => {
    if (!valor || valor.trim() === "") {
      return ""; // Vacío es válido (opcional)
    }
    
    const rucLimpio = valor.trim().replace(/\s+/g, '');
    
    // Validar formato: solo números y guiones
    if (!/^[\d-]+$/.test(rucLimpio)) {
      return "❌ El RUC solo debe contener números y guiones (sin espacios)";
    }
    
    // Verificar que no tenga múltiples guiones seguidos
    if (/--/.test(rucLimpio)) {
      return "❌ El RUC no puede tener guiones consecutivos";
    }
    
    return ""; // Válido
  };

  const validarEmail = (valor) => {
    if (!valor || valor.trim() === "") {
      return ""; // Vacío es válido (opcional)
    }
    
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(valor.trim())) {
      return "❌ Email inválido (debe contener @ y un dominio válido)";
    }
    
    return ""; // Válido
  };

  // Handler para RUC con validación
  const handleRUCChange = (e) => {
    const valor = e.target.value;
    setForm({ ...form, ruc: valor });
    
    const error = validarRUC(valor);
    setErrores(prev => ({ ...prev, ruc: error }));
  };

  // Handler para Email con validación
  const handleEmailChange = (e) => {
    const valor = e.target.value;
    setForm({ ...form, email: valor });
    
    const error = validarEmail(valor);
    setErrores(prev => ({ ...prev, email: error }));
  };

  const diasSemana = [
    { key: 'lunes', nombre: 'Lunes', abrev: 'L' },
    { key: 'martes', nombre: 'Martes', abrev: 'M' },
    { key: 'miercoles', nombre: 'Miércoles', abrev: 'X' },
    { key: 'jueves', nombre: 'Jueves', abrev: 'J' },
    { key: 'viernes', nombre: 'Viernes', abrev: 'V' },
    { key: 'sabado', nombre: 'Sábado', abrev: 'S' },
    { key: 'domingo', nombre: 'Domingo', abrev: 'D' }
  ];

  // Funciones de horarios (exactamente como en desktop)
  const actualizarHorario = (dia, campo, valor) => {
    setForm(prev => ({
      ...prev,
      horarios_laborales: {
        ...prev.horarios_laborales,
        [dia]: {
          ...prev.horarios_laborales[dia],
          [campo]: valor
        }
      }
    }));
  };

  const toggleDiaHorario = (dia) => {
    setForm(prev => ({
      ...prev,
      horarios_laborales: {
        ...prev.horarios_laborales,
        [dia]: {
          ...prev.horarios_laborales[dia],
          activo: !prev.horarios_laborales[dia]?.activo
        }
      }
    }));
  };

  const aplicarHorarioATodos = (horarioBase) => {
    const nuevosHorarios = {};
    diasSemana.forEach(dia => {
      nuevosHorarios[dia.key] = {
        activo: form.horarios_laborales[dia.key]?.activo || false,
        hora_inicio: horarioBase.hora_inicio,
        hora_fin: horarioBase.hora_fin
      };
    });
    setForm(prev => ({
      ...prev,
      horarios_laborales: nuevosHorarios
    }));
  };

  const restaurarHorariosDefecto = () => {
    setForm(prev => ({
      ...prev,
      horarios_laborales: getHorariosDefecto()
    }));
  };

  // Funciones de sucursales (exactamente como en desktop)
  function agregarSucursal() {
    if (!nuevaSucursal.trim()) return;
    const nuevaSucursalObj = {
      nombre: nuevaSucursal.trim(),
      ubicacion_ciudad: "",
      ubicacion_direccion: "",
      ubicacion_remota: false,
      distancia_km: 0,
      coordenadas_lat: null,
      coordenadas_lng: null,
      orden_prioridad: 0,
      observaciones: ""
    };
    setForm({ ...form, sucursales: [...form.sucursales, nuevaSucursalObj] });
    setNuevaSucursal("");
  }

  function eliminarSucursal(i) {
    const nuevas = form.sucursales.filter((_, idx) => idx !== i);
    setForm({ ...form, sucursales: nuevas });
  }

  function actualizarSucursal(sucursalData, index) {
    const nuevasSucursales = [...form.sucursales];
    nuevasSucursales[index] = sucursalData;
    setForm({ ...form, sucursales: nuevasSucursales });
  }

  // Función de verificación de bloqueos del sistema (exactamente como en desktop)
  const verificarBloqueosSistema = () => {
    const bloqueos = [];
    
    // Verificar iframe
    if (window.self !== window.top) {
      bloqueos.push('🖼️ La página está ejecutándose dentro de un iframe, lo que puede bloquear geolocalización');
    }
    
    // Verificar contexto seguro
    if (!window.isSecureContext) {
      bloqueos.push('🔒 El contexto no es seguro (no HTTPS), geolocalización bloqueada');
    }
    
    // Verificar políticas de permisos en headers
    const metaTags = document.querySelectorAll('meta[http-equiv]');
    metaTags.forEach(meta => {
      if (meta.httpEquiv === 'Permissions-Policy' || meta.httpEquiv === 'Feature-Policy') {
        const content = meta.content.toLowerCase();
        if (content.includes('geolocation') && (content.includes('none') || content.includes('self'))) {
          bloqueos.push('📋 Política de permisos en el HTML puede estar bloqueando geolocalización');
        }
      }
    });
    
    // Verificar Do Not Track
    if (navigator.doNotTrack === '1') {
      bloqueos.push('🚫 "Do Not Track" está activado, puede afectar permisos de ubicación');
    }
    
    // Verificar modo incógnito (aproximado)
    try {
      if (!window.indexedDB) {
        bloqueos.push('🕵️ Posible modo incógnito detectado, puede limitar permisos');
      }
    } catch (e) {
      bloqueos.push('🕵️ Posible modo privado/incógnito detectado');
    }
    
    return bloqueos;
  };

  const completarUbicacionAutomatica = () => {
    // Verificar disponibilidad de geolocalización
    if (!navigator.geolocation) {
      alert("La geolocalización no está disponible en este navegador. Puedes completar la ubicación manualmente.");
      return;
    }

    // Verificar bloqueos del sistema
    const bloqueosSistema = verificarBloqueosSistema();
    if (bloqueosSistema.length > 0) {
      console.warn('⚠️ Posibles bloqueos detectados:', bloqueosSistema);
    }

    // Verificar protocolo y mostrar información de depuración
    console.log('🔍 Información de geolocalización:');
    console.log('- Protocolo:', location.protocol);
    console.log('- Hostname:', location.hostname);
    console.log('- URL completa:', location.href);
    console.log('- Navegador soporta geolocalización:', !!navigator.geolocation);
    console.log('- Contexto seguro:', window.isSecureContext);
    console.log('- En iframe:', window.self !== window.top);
    console.log('- Posibles bloqueos:', bloqueosSistema);
    
    if (location.protocol !== 'https:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
      alert(`❌ Conexión no segura detectada
      
Protocolo actual: ${location.protocol}
URL: ${location.href}

La geolocalización requiere HTTPS. Por favor completa la ubicación manualmente.`);
      return;
    }

    setAutoUbicando(true);
    
    // Solicitar permisos explícitamente (versión completa como en desktop)
    navigator.permissions?.query({name: 'geolocation'}).then((result) => {
      console.log('Estado de permisos de geolocalización:', result.state);
      
      if (result.state === 'denied') {
        const userAgent = navigator.userAgent;
        const isChrome = userAgent.includes('Chrome');
        const isFirefox = userAgent.includes('Firefox');
        const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
        
        let instrucciones = '';
        
        if (isChrome) {
          instrucciones = `🌐 CHROME/EDGE - PERMISOS BLOQUEADOS DETECTADOS:

MÉTODO 1 - Desde la barra de direcciones:
1. Busca el ícono 🔒 o ❌ en la barra de direcciones
2. Haz clic en él
3. Busca "Ubicación" → Selecciona "Permitir"
4. Recarga la página (Ctrl+F5)

MÉTODO 2 - Si no aparece el ícono:
1. Copia y pega en una nueva pestaña: chrome://settings/content/location
2. En "Permitir", haz clic en "Agregar"
3. Escribe: ${window.location.origin}
4. Haz clic en "Agregar"
5. Vuelve a esta página y recarga (Ctrl+F5)

MÉTODO 3 - Reseteo completo (si nada funciona):
1. Ve a chrome://settings/content/location
2. Busca "${window.location.hostname}" en la lista "Bloquear"
3. Haz clic en los 3 puntos → "Eliminar"
4. Recarga esta página
5. Cuando pida permisos, selecciona "Permitir"

⚡ DESPUÉS DE CADA MÉTODO: Recarga la página completamente (Ctrl+F5)`;
        } else if (isFirefox) {
          instrucciones = `🦊 FIREFOX - Para habilitar ubicación:

1. Mira la barra de direcciones
2. Busca un ícono de escudo 🛡️ o información ⓘ
3. Haz clic en él
4. Encuentra "Ubicación" y selecciona "Permitir"
5. Si no aparece, ve a about:preferences#privacy
6. En "Permisos", busca "Ubicación" → "Configuración"
7. Encuentra ${window.location.hostname} y cambia a "Permitir"
8. Recarga esta página (F5)`;
        } else if (isSafari) {
          instrucciones = `🍎 SAFARI - Para habilitar ubicación:

1. Ve al menú Safari → Configuración
2. Pestaña "Sitios web"
3. Busca "Ubicación" en la lista izquierda
4. Encuentra ${window.location.hostname}
5. Cambia el menú a "Permitir"
6. Recarga esta página`;
        } else {
          instrucciones = `🔧 NAVEGADOR GENÉRICO:

1. Busca íconos en la barra de direcciones: 🔒 ❌ ⓘ 🛡️
2. Haz clic en cualquiera de estos íconos
3. Busca "Ubicación" o "Permisos"
4. Cambia a "Permitir" o "Siempre permitir"
5. Recarga la página (F5)

Si no funciona:
- Ve a configuración del navegador → Privacidad → Ubicación
- Agrega ${window.location.hostname} como permitido`;
        }
        
        if (isMobile) {
          instrucciones += `

📱 ADICIONAL PARA MÓVIL:
- Verifica que tu dispositivo tenga GPS activado
- En Configuración → Privacidad → Ubicación → Permitir para navegador
- Algunos dispositivos requieren reiniciar el navegador`;
        }
        
        const mensaje = `🚫 PERMISOS DE UBICACIÓN BLOQUEADOS

Estado: DENEGADO
Sitio: ${window.location.hostname}
Protocolo: ${location.protocol}

${instrucciones}

❗ IMPORTANTE: Debes recargar la página después de cambiar los permisos.`;
        
        if (confirm(mensaje + '\n\n¿Has cambiado los permisos? Presiona OK para recargar la página.')) {
          window.location.reload();
        }
        setAutoUbicando(false);
        return;
      }
      
      // Proceder con la geolocalización (versión completa)
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          try {
            const respuesta = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1`,
              {
                headers: {
                  'Accept-Language': 'es',
                  'User-Agent': 'Elements Client Management System'
                },
              }
            );

            if (!respuesta.ok) {
              throw new Error('No se pudo obtener la dirección desde el servicio de mapas.');
            }

            const data = await respuesta.json();
            const address = data?.address || {};
            const ciudadPrincipal = address.city || address.town || address.village || address.county || '';
            const departamento = address.state || address.region || address.state_district || '';
            const pais = address.country || 'Paraguay';

            setForm((prev) => ({
              ...prev,
              direccion: prev.direccion || data?.display_name || '',
              ciudad: prev.ciudad || ciudadPrincipal || '',
              ubicacion_ciudad: ciudadPrincipal || prev.ubicacion_ciudad || '',
              ubicacion_departamento: departamento || prev.ubicacion_departamento || '',
              ubicacion_pais: pais || prev.ubicacion_pais || 'Paraguay',
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6),
            }));
            
            alert('¡Ubicación detectada y completada automáticamente!');
          } catch (error) {
            console.error('Error obteniendo información de ubicación:', error);
            alert('Se detectó tu posición pero no se pudo obtener la dirección. Puedes completar los datos manualmente.');
            
            // Al menos guardar las coordenadas
            setForm((prev) => ({
              ...prev,
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6),
            }));
          } finally {
            setAutoUbicando(false);
          }
        },
        (geoError) => {
          console.error('Error de geolocalización:', geoError);
          let mensaje = 'No se pudo acceder a tu ubicación. ';
          
          switch(geoError.code) {
            case geoError.PERMISSION_DENIED:
              mensaje += `Permisos denegados. 

🔧 Para habilitar:
• Chrome: Haz clic en el candado 🔒 → Ubicación → Permitir
• Firefox: Haz clic en el escudo → Desbloquear ubicación  
• Safari: Configuración → Privacidad → Ubicación → Permitir

Luego recarga la página e intenta nuevamente.`;
              break;
            case geoError.POSITION_UNAVAILABLE:
              mensaje += 'La ubicación no está disponible en este momento.';
              break;
            case geoError.TIMEOUT:
              mensaje += 'La solicitud de ubicación tardó demasiado tiempo.';
              break;
            default:
              mensaje += 'Error desconocido al obtener la ubicación.';
          }
          
          alert(mensaje);
          setAutoUbicando(false);
        },
        {
          enableHighAccuracy: false, // Cambiado a false para mayor compatibilidad
          timeout: 20000, // Aumentado timeout
          maximumAge: 60000 // Reducido cache a 1 minuto
        }
      );
    }).catch((error) => {
      console.error('Error verificando permisos:', error);
      // Fallback: intentar directamente si no hay API de permisos (resto de la lógica igual que desktop)
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const respuesta = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1`,
              {
                headers: {
                  'Accept-Language': 'es',
                  'User-Agent': 'Elements Client Management System'
                },
              }
            );

            if (!respuesta.ok) throw new Error('Servicio de mapas no disponible');

            const data = await respuesta.json();
            const address = data?.address || {};
            const ciudadPrincipal = address.city || address.town || address.village || address.county || '';
            const departamento = address.state || address.region || address.state_district || '';
            const pais = address.country || 'Paraguay';

            setForm((prev) => ({
              ...prev,
              direccion: prev.direccion || data?.display_name || '',
              ciudad: prev.ciudad || ciudadPrincipal || '',
              ubicacion_ciudad: ciudadPrincipal || prev.ubicacion_ciudad || '',
              ubicacion_departamento: departamento || prev.ubicacion_departamento || '',
              ubicacion_pais: pais || prev.ubicacion_pais || 'Paraguay',
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6),
            }));
            
            alert('¡Ubicación completada automáticamente!');
          } catch (error) {
            console.error('Error en servicio de mapas:', error);
            setForm((prev) => ({
              ...prev,
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6),
            }));
            alert('Coordenadas guardadas. Completa manualmente ciudad y departamento.');
          } finally {
            setAutoUbicando(false);
          }
        },
        (geoError) => {
          console.error('Geolocalización falló:', geoError);
          alert('No se pudo detectar tu ubicación. Completa la información manualmente.');
          setAutoUbicando(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000
        }
      );
    });
  };

  const guardar = async () => {
    try {
      // Validar campos requeridos
      if (!form.nombre?.trim()) {
        alert("El nombre del cliente es requerido");
        return;
      }
      if (!form.tipo?.trim()) {
        alert("El tipo de cliente es requerido");
        return;
      }

      // Validar RUC si tiene valor
      if (form.ruc && form.ruc.trim()) {
        const errorRuc = validarRUC(form.ruc);
        if (errorRuc) {
          alert("Error en RUC: " + errorRuc.replace("❌ ", ""));
          return;
        }
      }

      // Validar email si tiene valor
      if (form.email && form.email.trim()) {
        const errorEmail = validarEmail(form.email);
        if (errorEmail) {
          alert("Error en Email: " + errorEmail.replace("❌ ", ""));
          return;
        }
      }

      // Asegurarse de que las sucursales sean una lista y datos estén correctos
      // Convertir coordenadas a números o null
      const coordenadas_lat = form.coordenadas_lat ? parseFloat(form.coordenadas_lat) : null;
      const coordenadas_lng = form.coordenadas_lng ? parseFloat(form.coordenadas_lng) : null;
      
      // Procesar sucursales: convertir coordenadas a números
      const sucursalesProcesadas = Array.isArray(form.sucursales) 
        ? form.sucursales.map(suc => ({
            ...suc,
            coordenadas_lat: suc.coordenadas_lat ? parseFloat(suc.coordenadas_lat) : null,
            coordenadas_lng: suc.coordenadas_lng ? parseFloat(suc.coordenadas_lng) : null
          }))
        : [];
      
      const dataToSend = {
        ...form,
        coordenadas_lat,
        coordenadas_lng,
        sucursales: sucursalesProcesadas,
        departamentos: Array.isArray(form.departamentos) ? form.departamentos : [],
        configuracion_ubicacion: form.configuracion_ubicacion || null,
        horas_contratadas: Number(form.horas_contratadas) || 0,
        // Asegurarse de que siempre enviemos horarios_laborales válidos
        horarios_laborales: form.horarios_laborales || getHorariosDefecto(),
        // Sanitize new fields to ensure they match backend schema (int or null, not empty string)
        tipo_frecuencia_mantenimiento: form.tipo_frecuencia_mantenimiento || null,
        dia_mantenimiento_preferido: form.dia_mantenimiento_preferido ? parseInt(form.dia_mantenimiento_preferido) : null,
        inicio_acumulacion_mes: form.inicio_acumulacion_mes ? parseInt(form.inicio_acumulacion_mes) : null,
        inicio_acumulacion_anio: form.inicio_acumulacion_anio ? parseInt(form.inicio_acumulacion_anio) : null,
      };

      console.log("Datos que se envían al backend:", dataToSend);

      if (cliente) {
        const id = cliente?._id || cliente?.id;
        if (!id) {
          throw new Error("ID de cliente no disponible para actualizar");
        }
        const response = await http.put(`/clientes/${id}`, dataToSend);
        if (!response.data) {
          throw new Error("No se recibió respuesta del servidor");
        }
      } else {
        const response = await http.post("/clientes", dataToSend);
        if (!response.data) {
          throw new Error("No se recibió respuesta del servidor");
        }
      }
      recargar();
      cerrar();
    } catch (err) {
      console.error("Error guardando cliente", err);
      const mensaje = err.response?.data?.detail || err.message || "Error al guardar cliente";
      alert(mensaje);
    }
  };

  const SectionHeader = ({ id, titulo, icono: Icono, isOpen }) => (
    <button
      type="button"
      onClick={() => setSeccionAbierta(isOpen ? null : id)}
      className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${
        isOpen 
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icono className="w-5 h-5" />
        <span className="font-medium text-left">{titulo}</span>
      </div>
      {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
    </button>
  );

  return (
    <div ref={modalContentRef} className="space-y-6">
      {/* Contenido del formulario mobile - ResponsiveModal ya proporciona el wrapper y header */}
      
      {/* Botones de acción en la parte superior para móvil */}
      <div className="flex items-center justify-end gap-2 pb-4 border-b">
        <button
          type="button"
          onClick={guardar}
          disabled={guardando}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {guardando ? "Guardando..." : "Guardar"}
        </button>
        
        <button
          type="button"
          onClick={cerrar}
          className="flex items-center justify-center w-10 h-10 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Contenido del formulario */}
      <div className="space-y-4">
          
          {/* Sección: Datos Básicos */}
          <div>
            <SectionHeader 
              id="basicos" 
              titulo="Datos Básicos" 
              icono={User}
              isOpen={seccionAbierta === 'basicos'} 
            />
            
            {seccionAbierta === 'basicos' && (
              <div className="mt-4 space-y-4 p-4 bg-emerald-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre de la Empresa *
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Nombre completo de la empresa"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    RUC *
                  </label>
                  <input
                    type="text"
                    className={`w-full border rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 ${
                      errores.ruc 
                        ? 'border-red-400 focus:ring-red-500 focus:border-red-500 bg-red-50' 
                        : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
                    }`}
                    placeholder="12345678-9 (sin espacios)"
                    value={form.ruc}
                    onChange={handleRUCChange}
                  />
                  {errores.ruc ? (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      {errores.ruc}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      ✓ Solo números y guiones, sin espacios
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Cliente
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  >
                    <option value="ocasional">Ocasional</option>
                    <option value="mensual">Mensual</option>
                    <option value="corporativo">Corporativo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    value={form.estado}
                    onChange={(e) => setForm({ ...form, estado: e.target.value })}
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Horas Contratadas
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="0"
                    value={form.horas_contratadas}
                    onChange={(e) => setForm({ ...form, horas_contratadas: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sección: Contacto */}
          <div>
            <SectionHeader 
              id="contacto" 
              titulo="Información de Contacto" 
              icono={Phone}
              isOpen={seccionAbierta === 'contacto'} 
            />
            
            {seccionAbierta === 'contacto' && (
              <div className="mt-4 space-y-4 p-4 bg-green-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Ej: +595 21 123456"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </label>
                  <input
                    type="email"
                    className={`w-full border rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 ${
                      errores.email 
                        ? 'border-red-400 focus:ring-red-500 focus:border-red-500 bg-red-50' 
                        : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
                    }`}
                    placeholder="empresa@ejemplo.com"
                    value={form.email}
                    onChange={handleEmailChange}
                  />
                  {errores.email ? (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      {errores.email}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      ✓ Debe contener @ y un dominio válido
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sección: Ubicación */}
          <div>
            <SectionHeader 
              id="ubicacion" 
              titulo="Ubicación y Dirección" 
              icono={Navigation}
              isOpen={seccionAbierta === 'ubicacion'} 
            />
            
            {seccionAbierta === 'ubicacion' && (
              <div className="mt-4 space-y-4 p-4 bg-purple-50 rounded-lg">
                {/* Botón de autocompletar ubicación */}
                <div className="bg-white p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-gray-600 mb-3">
                    🗺️ Completa automáticamente la ciudad y departamento usando tu ubicación actual
                  </p>
                  <button
                    type="button"
                    onClick={completarUbicacionAutomatica}
                    disabled={autoUbicando}
                    className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    {autoUbicando ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Obteniendo ubicación...
                      </>
                    ) : (
                      <>
                        <Crosshair className="w-5 h-5" />
                        Usar mi ubicación
                      </>
                    )}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dirección
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                    placeholder="Dirección completa..."
                    rows="3"
                    value={form.direccion}
                    onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ciudad
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Ej: Asunción"
                    value={form.ubicacion_ciudad || form.ciudad}
                    onChange={(e) => setForm({ ...form, ubicacion_ciudad: e.target.value, ciudad: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Departamento
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Ej: Central"
                    value={form.ubicacion_departamento}
                    onChange={(e) => setForm({ ...form, ubicacion_departamento: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    País
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Paraguay"
                    value={form.ubicacion_pais || "Paraguay"}
                    onChange={(e) => setForm({ ...form, ubicacion_pais: e.target.value })}
                  />
                </div>

                {/* Coordenadas GPS */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Latitud
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="-25.2637"
                      value={form.coordenadas_lat || ""}
                      onChange={(e) => setForm({ ...form, coordenadas_lat: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Longitud
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="-57.5759"
                      value={form.coordenadas_lng || ""}
                      onChange={(e) => setForm({ ...form, coordenadas_lng: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sección: Configuración Avanzada y Monitoreo */}
          <div>
            <SectionHeader 
              id="monitoreo" 
              titulo="Monitoreo y Avanzado" 
              icono={Settings}
              isOpen={seccionAbierta === 'monitoreo'} 
            />
            {seccionAbierta === 'monitoreo' && (
              <div className="mt-4 p-4 space-y-4 rounded-lg bg-gray-50 border border-gray-100">
                
                {/* Monitoreo Kuma */}
                <div className="bg-white p-4 rounded-lg shadow-sm">
                   <h4 className="text-sm font-medium text-gray-900 border-b pb-2 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Estado de Servicios (Kuma)
                  </h4>
                   <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      Enlace Página de Estado
                    </label>
                    <div className="flex rounded-md shadow-sm">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-200 bg-gray-50 text-gray-500 text-xs">
                        https://
                      </span>
                      <input
                        type="text"
                        className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md text-sm border-gray-200 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="kuma.elements.com.py..."
                        value={form.monitor_url ? form.monitor_url.replace(/^https?:\/\//, '') : ''}
                        onChange={(e) => setForm({...form, monitor_url: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

              </div>
            )}
           </div>

          {/* Sección: Horarios */}
          <div>
            <SectionHeader 
              id="horarios" 
              titulo="Horarios de Atención" 
              icono={Clock}
              isOpen={seccionAbierta === 'horarios'} 
            />
            
            {seccionAbierta === 'horarios' && (
              <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-3">
                  ⏰ Configura los horarios de atención para este cliente
                </p>
                <HorariosLaboralesForm
                  horarios={form.horarios_laborales || getHorariosDefecto()}
                  onUpdate={actualizarHorario}
                  onToggleDia={toggleDiaHorario}
                  onAplicarATodos={aplicarHorarioATodos}
                  onRestaurar={restaurarHorariosDefecto}
                />
              </div>
            )}
          </div>

          {/* Sección: Sucursales */}
          <div>
            <SectionHeader 
              id="sucursales" 
              titulo="Sucursales" 
              icono={Building2}
              isOpen={seccionAbierta === 'sucursales'} 
            />
            
            {seccionAbierta === 'sucursales' && (
              <div className="mt-4 p-4 bg-teal-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-3">
                  🏢 Gestiona las sucursales de este cliente
                </p>
                <SucursalForm
                  sucursales={form.sucursales || []}
                  nuevaSucursal={nuevaSucursal}
                  setNuevaSucursal={setNuevaSucursal}
                  agregarSucursal={agregarSucursal}
                  eliminarSucursal={eliminarSucursal}
                  actualizarSucursal={actualizarSucursal}
                  clienteId={cliente?._id || cliente?.id}
                />
              </div>
            )}
          </div>

          {/* Sección: Departamentos */}
          <div>
            <SectionHeader 
              id="departamentos" 
              titulo={form.configuracion_ubicacion?.label_departamento || 'Áreas / Departamentos'}
              icono={Layers}
              isOpen={seccionAbierta === 'departamentos'} 
            />
            
            {seccionAbierta === 'departamentos' && (
              <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
                {/* Toggle: usa departamentos */}
                <div className="flex flex-col gap-2 mb-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.configuracion_ubicacion?.usa_departamentos || false}
                      onChange={(e) => setForm({
                        ...form,
                        configuracion_ubicacion: {
                          ...form.configuracion_ubicacion,
                          usa_departamentos: e.target.checked
                        }
                      })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Usa departamentos/áreas
                  </label>
                  {form.configuracion_ubicacion?.usa_departamentos && (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.configuracion_ubicacion?.departamento_obligatorio || false}
                        onChange={(e) => setForm({
                          ...form,
                          configuracion_ubicacion: {
                            ...form.configuracion_ubicacion,
                            departamento_obligatorio: e.target.checked
                          }
                        })}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Obligatorio
                    </label>
                  )}
                </div>

                {form.configuracion_ubicacion?.usa_departamentos && (
                  <>
                    {/* Etiqueta */}
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Etiqueta</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        placeholder="Área, Departamento, Sector..."
                        value={form.configuracion_ubicacion?.label_departamento || ''}
                        onChange={(e) => setForm({
                          ...form,
                          configuracion_ubicacion: {
                            ...form.configuracion_ubicacion,
                            label_departamento: e.target.value
                          }
                        })}
                      />
                    </div>

                    {/* Lista */}
                    {(form.departamentos || []).map((dep, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2 bg-white rounded p-2 border">
                        <span className="text-sm font-medium flex-1">{dep.nombre}</span>
                        {dep.sucursal_nombre && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                            {dep.sucursal_nombre}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const depsCopy = [...form.departamentos];
                            depsCopy.splice(idx, 1);
                            setForm({ ...form, departamentos: depsCopy });
                          }}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          ✕
                        </button>
                      </div>
                    ))}

                    {/* Agregar */}
                    <div className="flex flex-col gap-2 mt-3">
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        placeholder="Nombre del departamento"
                        value={nuevoDepartamento.nombre}
                        onChange={(e) => setNuevoDepartamento({ ...nuevoDepartamento, nombre: e.target.value })}
                      />
                      {form.sucursales?.length > 0 && (
                        <select
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          value={nuevoDepartamento.sucursal_nombre}
                          onChange={(e) => setNuevoDepartamento({ ...nuevoDepartamento, sucursal_nombre: e.target.value })}
                        >
                          <option value="">Sin sucursal</option>
                          {form.sucursales.map((suc, i) => (
                            <option key={i} value={typeof suc === 'string' ? suc : suc.nombre}>
                              {typeof suc === 'string' ? suc : suc.nombre}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (!nuevoDepartamento.nombre.trim()) return;
                          setForm({
                            ...form,
                            departamentos: [
                              ...(form.departamentos || []),
                              { nombre: nuevoDepartamento.nombre.trim(), sucursal_nombre: nuevoDepartamento.sucursal_nombre || null, activo: true }
                            ]
                          });
                          setNuevoDepartamento({ nombre: "", sucursal_nombre: "" });
                        }}
                        className="bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700"
                      >
                        Agregar
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Sección: Observaciones */}
          <div>
            <SectionHeader 
              id="observaciones" 
              titulo="Observaciones Adicionales" 
              icono={FileText}
              isOpen={seccionAbierta === 'observaciones'} 
            />
            
            {seccionAbierta === 'observaciones' && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notas y Observaciones
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-none"
                    placeholder="Cualquier información adicional sobre el cliente..."
                    rows="4"
                    value={form.observaciones || ""}
                    onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sección: Configuración Avanzada (Solo Admin) */}
          {user?.rol === 'admin' && (
            <div>
              <SectionHeader 
                id="configuracion" 
                titulo="Configuración Avanzada" 
                icono={Settings}
                isOpen={seccionAbierta === 'configuracion'} 
              />
              
              {seccionAbierta === 'configuracion' && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-6">
                  {/* Horas Acumulables */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div>
                      <label className="font-medium text-gray-900">Horas Acumulables</label>
                      <p className="text-xs text-gray-500">Permitir acumular horas no usadas</p>
                    </div>
                    <div className="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
                      <input 
                        type="checkbox" 
                        name="toggle" 
                        id="toggle-horas" 
                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                        checked={form.horas_acumulables}
                        onChange={(e) => setForm({ ...form, horas_acumulables: e.target.checked })}
                        style={{ right: form.horas_acumulables ? '0' : 'auto', left: form.horas_acumulables ? 'auto' : '0', borderColor: form.horas_acumulables ? '#6366f1' : '#d1d5db' }}
                      />
                      <label 
                        htmlFor="toggle-horas" 
                        className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${form.horas_acumulables ? 'bg-teal-500' : 'bg-gray-300'}`}
                      ></label>
                    </div>
                  </div>

                  {form.horas_acumulables && (
                    <div className="grid grid-cols-2 gap-3 p-3 bg-teal-50 rounded-lg border border-teal-100">
                      <div>
                        <label className="block text-xs font-medium text-teal-900 mb-1">
                          Mes Inicio
                        </label>
                        <select
                          className="w-full border-teal-200 rounded-md text-sm focus:ring-teal-500 focus:border-teal-500"
                          value={form.inicio_acumulacion_mes}
                          onChange={(e) => setForm({ ...form, inicio_acumulacion_mes: parseInt(e.target.value) })}
                        >
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {new Date(0, i).toLocaleString('es', { month: 'long' })}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-teal-900 mb-1">
                          Año Inicio
                        </label>
                        <input
                          type="number"
                          className="w-full border-teal-200 rounded-md text-sm focus:ring-teal-500 focus:border-teal-500"
                          value={form.inicio_acumulacion_anio}
                          onChange={(e) => setForm({ ...form, inicio_acumulacion_anio: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                  )}

                  {/* Día Preferido Mantenimiento */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar size={16} className="text-gray-500" />
                      Preferencia de Mantenimiento
                    </label>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Frecuencia</label>
                        <select
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          value={form.tipo_frecuencia_mantenimiento || "mensual"}
                          onChange={(e) => setForm({ ...form, tipo_frecuencia_mantenimiento: e.target.value, dia_mantenimiento_preferido: null })}
                        >
                          <option value="mensual">Mensual (Día del mes)</option>
                          <option value="semanal">Semanal (Día de la semana)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          {form.tipo_frecuencia_mantenimiento === 'semanal' ? 'Día de la semana' : 'Día del mes'}
                        </label>
                        <select
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          value={form.dia_mantenimiento_preferido || ""}
                          onChange={(e) => setForm({ ...form, dia_mantenimiento_preferido: e.target.value ? parseInt(e.target.value) : null })}
                        >
                          <option value="">Sin preferencia</option>
                          {form.tipo_frecuencia_mantenimiento === 'semanal' ? (
                            <>
                              <option value="1">Lunes</option>
                              <option value="2">Martes</option>
                              <option value="3">Miércoles</option>
                              <option value="4">Jueves</option>
                              <option value="5">Viernes</option>
                              <option value="6">Sábado</option>
                              <option value="7">Domingo</option>
                            </>
                          ) : (
                            Array.from({ length: 31 }, (_, i) => (
                              <option key={i + 1} value={i + 1}>
                                Día {i + 1}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Día sugerido para agendar mantenimientos recurrentes.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
}